import { sendRedirect } from 'h3'
import { Link, PageQueryDocument, PageQueryQuery } from '../graphql/generated'
import { isPast } from 'date-fns'
import { toZonedTime } from 'date-fns-tz'
import { locales, defaultLocale, type LocaleCode } from '~/i18n/config'

// export type LanguageSwitchLinks = Extract<
//   PageQueryQuery['route'],
//   { __typename: 'EntityCanonicalUrl' }
// >['languageSwitchLinks']

const supportedEntities = [
  'NodePage',
  'NodeOpenJob',
  'NodeLandingPage',
  'NodeCbzLandingPage',
  'NodeProtectedPage',
  'NodeNews',
  'NodeEvent',
  'Employee',
  'ToccoSeminar',
  'ToccoModule',
  'ToccoIntroModule',
  'ToccoELearning',
  'ToccoCourse',
  'ToccoWebinar',
]

const shouldDisplayPage = (route: PageQueryQuery['route']) => {
  if (!route) return false
  if (route.__typename !== 'EntityCanonicalUrl') return false
  if (!route.entity) return false
  if (!route.entity.__typename) return false

  // employee is not visible if visible from is in the future
  if (route.entity.__typename === 'Employee' && route.entity.fieldVisibleFrom) {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone
    const visibleFrom = toZonedTime(route.entity.fieldVisibleFrom.date, tz)
    if (!isPast(visibleFrom)) return false
  }
  if (!supportedEntities.includes(route.entity.__typename)) return false

  return true
}

const maybeRedirectToCanonicalUrl = (currentPath: string, route: PageQueryQuery['route']) => {
  if (process.server) return

  const enabledLanguages = locales.filter((locale) => locale.code !== defaultLocale).map((locale) => locale.code)
  // Split route in parts by "/"
  const routePathParts = currentPath.split('/').filter(Boolean)

  // Remove first element (language prefix)
  if (routePathParts[0] && enabledLanguages.includes(routePathParts[0] as LocaleCode)) {
    routePathParts.shift()
  }
  const routePathNoPrefix = '/' + routePathParts.join('/')
  if (
    routePathNoPrefix !== '/' &&
    route?.__typename === 'EntityCanonicalUrl' &&
    route.pathAlias &&
    route.pathAlias !== routePathNoPrefix
  ) {
    navigateTo(route.path)
  }
}

const getLayoutName = (route: PageQueryQuery['route']) => {
  if (!route) return 'default'
  if (route.__typename !== 'EntityCanonicalUrl') return 'default'
  if (route.entity?.__typename === 'NodeCbzLandingPage') return 'cbz' // the name of the layout file
  if (route.entity?.__typename === 'NodeProtectedPage') return 'protected' // the name of the layout file

  return 'default'
}

const getData = async (
  query: (options: {
    query: typeof PageQueryDocument
    variables: { path: string }
  }) => Promise<{ data: PageQueryQuery }>,
  path: string,
) => {
  const cacheKey = `page-${path}${process.server && '-server'}`

  const response = await useAsyncData(cacheKey, async () => {
    const { data } = await query({
      query: PageQueryDocument,
      variables: { path },
    })
    return data
  })

  return {
    data: computed(() => response.data.value || undefined),
  }
}

export const useRouteLayout = async (path = '/') => {
  const { clients } = useApollo()

  const { data } = await getData(clients!.default.query, path)

  const layout = getLayoutName(data.value?.route)

  return { layout }
}

export const useRouteData = async (path = '/') => {
  const { clients } = useApollo()

  // Capture the request event before any await: the Nuxt instance context can
  // get lost across async boundaries, which used to turn both the redirect and
  // its cache guard below into silent no-ops, so the redirect rendered as a
  // 200 empty shell and got cached (CARW-1677).
  const event = process.server ? useRequestEvent() : undefined

  const { data } = await getData(clients!.default.query, path)

  // Redirect to target path if redirect is set
  // TODO(CARW-1677): once the frontend-update branch (with test infra) lands,
  // add e2e coverage: old URL of a renamed page answers 301/302 on first
  // request (server), and a client-side navigation onto an old URL ends on the
  // fully rendered target page.
  if (data.value?.route && data.value.route.__typename === 'RedirectUrl') {
    if (data.value.route.target && data.value.route.target.path) {
      if (process.server) {
        if (!event) {
          // Fail loudly instead of rendering the redirect as a cacheable shell.
          throw createError({ statusCode: 500, statusMessage: 'Request event unavailable, cannot send redirect' })
        }
        // Mark as redirect so the response is not cached
        event.node.res.setHeader('X-Drupal-Redirect', 'true')
        // Send the redirect directly on the captured event; navigateTo needs
        // the instance context and failed silently when it was lost.
        await sendRedirect(event, data.value.route.target.path, data.value.route.code || 301)
        return { entity: null, breadcrumbs: [] }
      }
      // Client side: navigate as a full page load so the target is server
      // rendered. An in-setup SPA navigation lands on a reused catch-all
      // instance that never fetches the target's data, leaving the content
      // area empty (CARW-1677).
      await navigateTo(data.value.route.target.path, { external: true })
      return { entity: null, breadcrumbs: [] }
    }
  }

  // Throw 404 if page is not found
  if (!shouldDisplayPage(data?.value?.route)) {
    throw createError({ statusCode: 404, statusMessage: 'Page Not Found', fatal: true })
  }

  maybeRedirectToCanonicalUrl(path, data.value?.route)

  const { setDrupal } = useLanguageSwitcherStore()

  const links =
    data.value?.route?.__typename === 'EntityCanonicalUrl'
      ? [...(data.value.route.languageSwitchLinks || [])]
          .map((link) => {
            if (!link) return null
            return {
              locale: link.language?.id || '',
              path: link.url?.path || '',
            }
          })
          .filter(Boolean)
      : []
  setDrupal(links)

  const breadcrumbs =
    (data.value?.route?.__typename === 'EntityCanonicalUrl' &&
      data.value?.route?.breadcrumb?.filter(Boolean).map((item: Link) => {
        return {
          text: item.text || '',
          url: item.url?.path || '',
        }
      })) ||
    []

  return {
    entity: data.value?.route?.__typename === 'EntityCanonicalUrl' && data.value?.route?.entity,
    breadcrumbs,
  }
}
